// frontend/src/components/common/NotificationBell.js
// In-app notification bell. The header previously showed a hardcoded 0.
import React, { useCallback, useEffect, useState } from 'react';
import {
    IconButton, Badge, Tooltip, Menu, MenuItem, ListItemText, Typography,
    Divider, Box, Button, CircularProgress,
} from '@mui/material';
import { Notifications, DoneAll } from '@mui/icons-material';
import { useNavigate } from 'react-router-dom';

import api from '../../services/api';
import socket from '../../services/socket';

const POLL_MS = 60 * 1000;

const NotificationBell = () => {
    const navigate = useNavigate();
    const [anchor, setAnchor] = useState(null);
    const [items, setItems] = useState([]);
    const [count, setCount] = useState(0);
    const [loading, setLoading] = useState(false);

    const loadCount = useCallback(async () => {
        try {
            const res = await api.get('/notifications/unread-count');
            setCount(res.data.count || 0);
        } catch {
            /* keep the last known count */
        }
    }, []);

    const loadItems = useCallback(async () => {
        setLoading(true);
        try {
            const res = await api.get('/notifications?limit=20');
            setItems(res.data || []);
        } catch {
            setItems([]);
        } finally {
            setLoading(false);
        }
    }, []);

    useEffect(() => {
        loadCount();
        const id = setInterval(loadCount, POLL_MS);
        // A status change anywhere may have produced a notification for us.
        const onEvent = () => loadCount();
        ['statusUpdate', 'partCreated', 'ticketStatusUpdate'].forEach(e => socket.on(e, onEvent));
        return () => {
            clearInterval(id);
            ['statusUpdate', 'partCreated', 'ticketStatusUpdate'].forEach(e => socket.off(e, onEvent));
        };
    }, [loadCount]);

    const open = (event) => {
        setAnchor(event.currentTarget);
        loadItems();
    };

    const markAll = async () => {
        try {
            await api.put('/notifications/read-all');
            setCount(0);
            loadItems();
        } catch {
            /* ignore */
        }
    };

    const clickItem = async (n) => {
        try {
            if (!n.is_read) {
                await api.put(`/notifications/${n.id}/read`);
                loadCount();
            }
        } catch {
            /* navigation still makes sense */
        }
        setAnchor(null);
        if (n.part_number) navigate(`/parts?search=${encodeURIComponent(n.part_number)}`);
        else if (n.ticket_key) navigate('/tickets');
    };

    return (
        <>
            <Tooltip title="Notifications">
                <IconButton color="inherit" onClick={open}>
                    <Badge badgeContent={count} color="error" max={99}>
                        <Notifications />
                    </Badge>
                </IconButton>
            </Tooltip>
            <Menu
                anchorEl={anchor}
                open={!!anchor}
                onClose={() => setAnchor(null)}
                anchorOrigin={{ vertical: 'bottom', horizontal: 'right' }}
                transformOrigin={{ vertical: 'top', horizontal: 'right' }}
                slotProps={{ paper: { sx: { width: 380, maxHeight: 460 } } }}
            >
                <Box sx={{
                    px: 2, py: 1, display: 'flex',
                    justifyContent: 'space-between', alignItems: 'center',
                }}>
                    <Typography variant="subtitle2" fontWeight={700}>
                        Notifications{count > 0 ? ` (${count} new)` : ''}
                    </Typography>
                    {count > 0 && (
                        <Button size="small" startIcon={<DoneAll />} onClick={markAll}>
                            Mark all read
                        </Button>
                    )}
                </Box>
                <Divider />

                {loading && (
                    <Box sx={{ py: 3, display: 'grid', placeItems: 'center' }}>
                        <CircularProgress size={22} />
                    </Box>
                )}

                {!loading && items.length === 0 && (
                    <MenuItem disabled>
                        <ListItemText primary="Nothing to show yet" />
                    </MenuItem>
                )}

                {!loading && items.map(n => (
                    <MenuItem key={n.id} onClick={() => clickItem(n)}
                        sx={{
                            whiteSpace: 'normal', alignItems: 'flex-start',
                            bgcolor: n.is_read ? undefined : 'action.hover',
                        }}>
                        <ListItemText
                            primary={
                                <Typography variant="body2"
                                    fontWeight={n.is_read ? 400 : 600}>
                                    {n.message}
                                </Typography>
                            }
                            secondary={new Date(n.created_at).toLocaleString('en-GB')}
                        />
                    </MenuItem>
                ))}
            </Menu>
        </>
    );
};

export default NotificationBell;
